import { supabase } from '../supabaseClient';
import { downloadTextFile } from './exportUtils';

function nowStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) + '-' +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds())
  );
}

// 增强：支持仅最新与选择导出表
export async function exportDeviceLibraryToJson(options = {}) {
  const defaultTables = ['ground_types', 'buses', 'transformers', 'lines', 'cts', 'pts', 'relays', 'feeders'];
  const cfg = { onlyLatest: false, tables: defaultTables, fileNamePrefix: 'device-library', ...options };
  const result = {};
  const counts = {};

  const latestFilter = (tableName, rows) => {
    if (!cfg.onlyLatest) return rows;
    const keySelector = (r) => {
      if (tableName === 'feeders') return r.label || r.name || r.id;
      return r.name || r.label || r.id;
    };
    const map = new Map();
    (rows || []).forEach(r => {
      const key = keySelector(r);
      const current = map.get(key);
      if (!current || (r.version || 0) > (current.version || 0)) {
        map.set(key, r);
      }
    });
    return Array.from(map.values());
  };

  for (const t of cfg.tables) {
    try {
      // 始终从基础表导出，避免视图字段造成导入失败
      const { data, error } = await supabase.from(t).select('*');
      if (error) {
        result[t] = [];
        counts[t] = 0;
        continue;
      }
      const rows = data || [];
      const filtered = latestFilter(t, rows);
      result[t] = filtered;
      counts[t] = filtered.length;
    } catch (err) {
      result[t] = [];
      counts[t] = 0;
    }
  }

  const payload = { exported_at: new Date().toISOString(), onlyLatest: cfg.onlyLatest, tables: result };
  const content = JSON.stringify(payload, null, 2);
  const filename = `${cfg.fileNamePrefix || 'device-library'}-${nowStamp()}.json`;
  // 参数顺序：text, filename
  downloadTextFile(content, filename);
  return { filename, counts, tableCount: Object.keys(result).length, onlyLatest: cfg.onlyLatest };
}

// ---------------------- 新增：导入校验与规范化 ----------------------
function isNum(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

function normalizeTableRows(tableName, rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => {
    const o = { ...r };
    if (tableName === 'cts') {
      if (o.ct_ratio != null && o.ratio == null) o.ratio = o.ct_ratio;
    }
    if (tableName === 'pts') {
      if (o.pt_ratio != null && o.ratio == null) o.ratio = o.pt_ratio;
    }
    return o;
  });
}

function validateRow(tableName, row) {
  const errs = [];
  const req = (cond, msg) => { if (!cond) errs.push(msg); };
  if (tableName === 'buses') {
    req(typeof row.name === 'string' && row.name.trim().length > 0, 'buses.name 必填');
    req(isNum(row.voltage_kV) && row.voltage_kV > 0, 'buses.voltage_kV 必须为正数');
  } else if (tableName === 'transformers') {
    req(typeof row.name === 'string' && row.name.trim().length > 0, 'transformers.name 必填');
    req(isNum(row.S_tr_MVA) && row.S_tr_MVA > 0, 'transformers.S_tr_MVA 必须为正数');
    req(isNum(row.uk_percent) && row.uk_percent >= 0, 'transformers.uk_percent 必须为非负数');
  } else if (tableName === 'lines') {
    req(typeof row.name === 'string' && row.name.trim().length > 0, 'lines.name 必填');
    req(isNum(row.length_km) && row.length_km >= 0, 'lines.length_km 必须为非负数');
    req(isNum(row.R_per_km_ohm) && row.R_per_km_ohm >= 0, 'lines.R_per_km_ohm 必须为非负数');
    req(isNum(row.X_per_km_ohm) && row.X_per_km_ohm >= 0, 'lines.X_per_km_ohm 必须为非负数');
  } else if (tableName === 'cts') {
    req(typeof row.name === 'string' && row.name.trim().length > 0, 'cts.name 必填');
    req(isNum(row.ratio) && row.ratio > 0, 'cts.ratio 必须为正数');
  } else if (tableName === 'pts') {
    req(typeof row.name === 'string' && row.name.trim().length > 0, 'pts.name 必填');
    req(isNum(row.ratio) && row.ratio > 0, 'pts.ratio 必须为正数');
  } else if (tableName === 'relays') {
    // 允许仅提供 model
    req(typeof row.model === 'string' && row.model.trim().length > 0, 'relays.model 必填');
  } else if (tableName === 'ground_types') {
    req(typeof row.key === 'string' && row.key.trim().length > 0, 'ground_types.key 必填');
    // default_R_ohm / default_X_ohm 可选，但若提供需为数值
    if (row.default_R_ohm != null) req(isNum(row.default_R_ohm), 'ground_types.default_R_ohm 必须为数值');
    if (row.default_X_ohm != null) req(isNum(row.default_X_ohm), 'ground_types.default_X_ohm 必须为数值');
  } else if (tableName === 'feeders') {
    req(typeof row.label === 'string' && row.label.trim().length > 0, 'feeders.label 必填');
    req(typeof row.bus_id === 'string', 'feeders.bus_id 必填');
    req(typeof row.transformer_id === 'string', 'feeders.transformer_id 必填');
    // 其他字段可选
    if (row.rated_current != null) req(isNum(row.rated_current) && row.rated_current >= 0, 'feeders.rated_current 必须为非负数');
    if (row.ct_ratio != null) req(isNum(row.ct_ratio) && row.ct_ratio > 0, 'feeders.ct_ratio 必须为正数');
  }
  return errs;
}

function validateDeviceLibraryTables(tables) {
  const tableNames = ['ground_types','buses','transformers','lines','cts','pts','relays','feeders'];
  const summary = [];
  const validRows = {};
  const invalidRows = {};
  let totalErrors = 0;

  for (const t of tableNames) {
    const rowsRaw = tables?.[t] || [];
    const rows = normalizeTableRows(t, rowsRaw);
    const errorsForTable = [];
    const valids = [];

    rows.forEach((row, idx) => {
      const errs = validateRow(t, row);
      if (errs.length > 0) {
        errorsForTable.push({ index: idx, messages: errs });
        totalErrors += errs.length;
      } else {
        valids.push(row);
      }
    });

    validRows[t] = valids;
    invalidRows[t] = errorsForTable;
    summary.push({ table: t, total: rows.length, valid: valids.length, invalid: errorsForTable.length });
  }

  return { summary, validRows, invalidRows, totalErrors };
}

export async function importDeviceLibraryFromJson(json, options = {}) {
  const cfg = { mode: 'upsert', dryRun: false, ...options };
  let payload = json;
  if (typeof json === 'string') {
    try {
      payload = JSON.parse(json);
    } catch (e) {
      throw new Error('导入文件不是有效的 JSON');
    }
  }
  const tables = payload?.tables || payload || {};
  const validation = validateDeviceLibraryTables(tables);
  const report = { validation, dbResults: {}, success: [], failed: [] };

  async function writeTable(tableName, rows, onConflict) {
    const valid = Array.isArray(rows) ? rows : [];
    if (cfg.dryRun) {
      report.dbResults[tableName] = { insertedCount: valid.length, upsertedCount: valid.length, failedCount: 0, dryRun: true };
      return;
    }
    if (!valid.length) {
      report.dbResults[tableName] = { insertedCount: 0, upsertedCount: 0, failedCount: 0 };
      return;
    }
    try {
      if (cfg.mode === 'insert') {
        const { error } = await supabase.from(tableName).insert(valid);
        if (error) throw error;
        report.dbResults[tableName] = { insertedCount: valid.length, upsertedCount: 0, failedCount: 0 };
      } else {
        const { error } = await supabase.from(tableName).upsert(valid, onConflict ? { onConflict } : undefined);
        if (error) throw error;
        report.dbResults[tableName] = { insertedCount: 0, upsertedCount: valid.length, failedCount: 0 };
      }
    } catch (err) {
      report.dbResults[tableName] = { insertedCount: 0, upsertedCount: 0, failedCount: valid.length, error: err?.message || 'write failed' };
      report.failed.push({ table: tableName, error: err?.message || String(err) });
    }
  }

  await writeTable('ground_types', validation.validRows['ground_types'], 'key');
  await writeTable('buses', validation.validRows['buses']);
  await writeTable('transformers', validation.validRows['transformers']);
  await writeTable('lines', validation.validRows['lines']);
  await writeTable('cts', validation.validRows['cts']);
  await writeTable('pts', validation.validRows['pts']);
  await writeTable('relays', validation.validRows['relays']);
  await writeTable('feeders', validation.validRows['feeders']);

  report.meta = { imported_at: new Date().toISOString(), mode: cfg.mode, dryRun: cfg.dryRun };
  return report;
}

// ---------------------- 新增：模板下载 ----------------------
export function downloadDeviceLibraryTemplateJson() {
  const tmpl = {
    exported_at: new Date().toISOString(),
    tables: {
      ground_types: [ { key: 'solid', label: '实心接地', default_R_ohm: 0, default_X_ohm: 0 } ],
      buses: [ { name: 'BusA', voltage_kV: 10, version: 1, status: 'active' } ],
      transformers: [ { name: 'TR1', S_tr_MVA: 31.5, uk_percent: 8, vector_group: 'Dyn11', version: 1, status: 'active' } ],
      lines: [ { name: 'L1', length_km: 2.5, R_per_km_ohm: 0.25, X_per_km_ohm: 0.35, z0_factor: 3, version: 1, status: 'active' } ],
      cts: [ { name: 'CT1', ratio: 200, version: 1, status: 'active' } ],
      pts: [ { name: 'PT1', ratio: 40, version: 1, status: 'active' } ],
      relays: [ { name: 'REL1', model: 'P141', version: 1, status: 'active' } ],
      feeders: [ { label: 'FDR-01', name: 'Feeder-1', bus_id: '<<bus_id>>', transformer_id: '<<transformer_id>>', ct_id: '<<ct_id>>', pt_id: '<<pt_id>>', relay_id: '<<relay_id>>', rated_current: 400, ct_ratio: 200, source_S_sc_MVA: 500, source_X_R: 10, ground_R_ohm: 0.5, ground_X_ohm: 0.1, version: 1, status: 'active' } ]
    }
  };
  const content = JSON.stringify(tmpl, null, 2);
  const filename = `device-library-template-${nowStamp()}.json`;
  // 注意参数顺序：text, filename
  downloadTextFile(content, filename);
  return { filename };
}